import React from 'react';
import PropTypes from 'prop-types';
import CSSModules from 'react-css-modules';
import {autobind} from 'core-decorators';
import {FormattedMessage, injectIntl, intlShape} from 'react-intl';
import classNames from 'classnames';
import RaisedButton from 'components/common/material/RaisedButton';
import Tooltip from 'components/common/material/Tooltip';
import IconButton from 'components/common/material/IconButton';
import * as FormatUtils from 'utils/formatUtils';
import * as sunbitConstants from 'constants/sunbit';
import * as purchaseConstants from './constants';
import {isPurchaseActive, getPurchaseInActiveReason, getPurchaseMergedStatus} from './helpers';
import Dialog from 'components/common/material/Dialog';
import styles from './payoffPayNow.scss';
import {datadogRum} from "@datadog/browser-rum";

const IconButtonTooltip = Tooltip(IconButton);

@injectIntl
@CSSModules(styles, {allowMultiple: true})
class PayoffPayNow extends React.Component {
  static propTypes = {
    intl: intlShape,
    purchase: PropTypes.object.isRequired,
    cssVariant: PropTypes.string,
    togglePayNow: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      notPayableDialogVisible: false,
      promotionalDialogVisible: false
    };
  }

  isLatePaymentPurchase() {
    const {purchase: {allPaymentsIncludingDownPayment}} = this.props;
    for (const payment of allPaymentsIncludingDownPayment) {
      if (payment.status.toUpperCase() === 'LATE') {
        return true;
      }
    }
    return false;
  }

  @autobind
  toggleNotPayableDialogVisible() {
    if (process.env.NODE_ENV !== "automation" && !this.state.notPayableDialogVisible){
      try {
        datadogRum.addUserAction('toggleNotPayableDialogVisible', {
          event: {
            paymentMethodType : this.props.purchase.allPaymentsIncludingDownPayment.length > 0 ? this.props.purchase.allPaymentsIncludingDownPayment.map(item => {return { paymentMethodType : item.paymentMethodType , status : item.status , id : item.id };})  : '',
            externalId: this.props.purchase.externalId || 0,
            status : this.props.purchase.status || '',
            paymentPlanStatus: this.props.purchase.paymentPlanStatus || '',
            payable : this.props.purchase.payable,
          },
          customerId: this.props.purchase.customerId,
        });
        // eslint-disable-next-line no-empty
      } catch (e) {
        console.info(JSON.stringify(e));
      }
    }
    this.setState({notPayableDialogVisible: !this.state.notPayableDialogVisible});
  }

  @autobind
  togglePromotionalDialog() {
    this.setState({promotionalDialogVisible: !this.state.promotionalDialogVisible});
  }

  @autobind
  payNowClickHandler() {
    // TODO: can we combine all these cases??
    if (getPurchaseMergedStatus(this.props.purchase) === purchaseConstants.PURCHASE_STATUS_MERGED_LATE) {
      this.props.togglePayNow();
    }
    else if (!this.props.purchase.payable && this.props.purchase.paymentPlanStatus === purchaseConstants.PAYMENT_PLAN_STATUS_CHARGE_FAILED) {
      this.props.togglePayNow();
    }
    else if (this.props.purchase.payable) {
      this.props.togglePayNow();
    }
    else if (this.props.purchase.status === purchaseConstants.PURCHASE_STATUS_PAUSE_PAYMENTS_CHARGING && this.isLatePaymentPurchase() ) {
      this.props.togglePayNow();
    }
    else if (this.props.purchase.status === purchaseConstants.PURCHASE_STATUS_PAUSE_PAYMENTS_CHARGING ) {
      this.props.togglePayNow();
    }
    else {
      this.toggleNotPayableDialogVisible();
    }
  }

  renderPayNowNotPayableDialog() {

    const {intl: {formatMessage}} = this.props;

    const actions = [
      {onClick: this.toggleNotPayableDialogVisible, label: formatMessage({id: 'common.close'})}
    ];

    return (
      <Dialog active={this.state.notPayableDialogVisible} actions={actions}>
        <FormattedMessage id="purchase.notPayable.notice" values={{phoneNumber: sunbitConstants.PHONE_NUMBER}}/>
      </Dialog>
    );
  }

  renderPayOffAmountInfo() {
    const {intl: {formatMessage}} = this.props;

    return (
      <IconButtonTooltip styleName="payOffAmountInfoButton" tooltipShowOnClick={true} icon="info_outline" tooltip={formatMessage({id: 'purchase.info.label.payoffAmountInfo'})} />
    );
  }

  renderPromotionalDialog() {
    const {purchase, intl:{formatMessage}} = this.props;
    const {zeroAprPromotionExpirationDate} = purchase;
    const values = {
      date: FormatUtils.transformDate(zeroAprPromotionExpirationDate)
    };

    const actions = [
      {onClick: this.togglePromotionalDialog, primary: true, label: formatMessage({id: 'common.ok'})},
    ];

    return (
      <Dialog actions={actions} active={true}>
        <FormattedMessage id="purchase.details.promotional.details" values={values}/>
      </Dialog>
    );
  }

  renderPurchaseActive() {
    const {purchase, cssVariant} = this.props;
    const className = classNames('payoffPayNow', cssVariant);
    return (
      <div styleName={className}>
        <div styleName="payoffAmount">
          <div><FormattedMessage id="purchase.info.label.payoffAmount"/>{this.renderPayOffAmountInfo()}</div>
          {purchase.isZeroAprPromoted && <div><span styleName="promotional" onClick={this.togglePromotionalDialog}><FormattedMessage id="payNow.schedule.zeroApr.text"/></span></div> }
          <div data-at="outstandingAmount" styleName="amount">{FormatUtils.addPriceSymbol(purchase.leftAmountToPay)}</div>
        </div>

        <div styleName="actions">
          <RaisedButton data-at="payOff" styleName="payNowButton" primary={true} onClick={this.payNowClickHandler}><FormattedMessage id="purchase.action.label.payNow"/></RaisedButton>
        </div>
        {this.renderPayNowNotPayableDialog()}
        { this.state.promotionalDialogVisible && this.renderPromotionalDialog() }
      </div>
    );
  }

  renderPurchaseInactive() {
    const {purchase, cssVariant, intl:{formatMessage}} = this.props;
    const className = classNames('payoffPayNow', cssVariant);
    const inActiveReason = getPurchaseInActiveReason(purchase);

    return (
      <div styleName={className} className={styles[purchase.paymentPlanStatus.toLowerCase()]}>
        <span styleName="inActiveIcon" />
        <div styleName="inActiveReason">
          {
            inActiveReason &&
            formatMessage({id: inActiveReason})
          }
        </div>
      </div>
    );
  }

  render() {
    const {purchase} = this.props;
    return isPurchaseActive(purchase) ? this.renderPurchaseActive() : this.renderPurchaseInactive();
  }
}

export default PayoffPayNow;



// WEBPACK FOOTER //
// ./src/purchases/PayoffPayNow.js